/**
 * Jubly Calendar Week View — Google Calendar style
 * 7-day time grid with drag-to-create and drag-to-move events
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const HOUR_HEIGHT = 60;
const SNAP_MINUTES = 15;

export default function WeekView({
  currentDate,
  events,
  calendars,
  onEventClick,
  onTimeSlotClick,
  onEventUpdate,
  onCreateEventRange
}) {
  const scrollRef = useRef(null);
  const gridRef = useRef(null);

  // Drag-to-create state
  const [dragCreate, setDragCreate] = useState(null); // { dayIdx, startY, currentY }
  // Drag-to-move state
  const [dragMove, setDragMove] = useState(null); // { event, offsetY, dayIdx, currentTop }

  const weekDays = useMemo(() => {
    const days = [];
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());
    start.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [currentDate]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 8 * HOUR_HEIGHT;
    }
  }, []);

  const today = new Date();
  const isToday = (date) =>
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
  };

  // Convert pixel Y to snapped hour value
  const yToHour = useCallback((y) => {
    const rawHour = y / HOUR_HEIGHT;
    const snapped = Math.round(rawHour * (60 / SNAP_MINUTES)) / (60 / SNAP_MINUTES);
    return Math.max(0, Math.min(24, snapped));
  }, []);

  // Convert hour to time string
  const hourToTime = (hour) => {
    const h = Math.floor(hour);
    const m = Math.round((hour - h) * 60);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayH}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  const getEventsForDay = (date) => {
    const dayStart = new Date(date); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date); dayEnd.setHours(23, 59, 59, 999);

    const dayEvents = events.filter(event => {
      const s = new Date(event.start_time);
      const e = new Date(event.end_time);
      return s <= dayEnd && e >= dayStart;
    }).map(event => {
      const s = new Date(event.start_time);
      const e = new Date(event.end_time);
      let startH = s.getHours() + s.getMinutes() / 60;
      let endH = e.getHours() + e.getMinutes() / 60;
      if (s < dayStart) startH = 0;
      if (e > dayEnd) endH = 24;
      return {
        ...event,
        startH, endH,
        top: startH * HOUR_HEIGHT,
        height: Math.max((endH - startH) * HOUR_HEIGHT, 22),
        isAllDay: event.is_all_day || (endH - startH) >= 24
      };
    });

    const timed = dayEvents.filter(e => !e.isAllDay).sort((a, b) => a.startH - b.startH || b.height - a.height);
    const columns = [];
    for (const ev of timed) {
      let placed = false;
      for (let c = 0; c < columns.length; c++) {
        const last = columns[c][columns[c].length - 1];
        if (ev.startH >= last.endH) {
          columns[c].push(ev);
          ev._col = c;
          placed = true;
          break;
        }
      }
      if (!placed) {
        ev._col = columns.length;
        columns.push([ev]);
      }
    }
    const totalCols = columns.length || 1;
    for (const ev of timed) ev._totalCols = totalCols;

    return { allDay: dayEvents.filter(e => e.isAllDay), timed };
  };

  const getColor = (event) => event.calendar_color || event.color || '#3B82F6';
  const lighten = (hex, amount = 0.88) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgb(${Math.round(r + (255 - r) * amount)}, ${Math.round(g + (255 - g) * amount)}, ${Math.round(b + (255 - b) * amount)})`;
  };

  // --- Drag to create ---
  const handleGridMouseDown = (e, dayIdx) => {
    if (e.button !== 0) return;
    if (dragMove) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    setDragCreate({ dayIdx, startY: y, currentY: y });
  };

  const handleGridMouseMove = useCallback((e) => {
    if (dragCreate) {
      const cols = gridRef.current?.querySelectorAll('[data-day-col]');
      if (cols?.[dragCreate.dayIdx]) {
        const rect = cols[dragCreate.dayIdx].getBoundingClientRect();
        setDragCreate(prev => ({ ...prev, currentY: e.clientY - rect.top }));
      }
    }
    if (dragMove) {
      const cols = gridRef.current?.querySelectorAll('[data-day-col]');
      if (cols) {
        // Determine which day column the mouse is over
        let newDayIdx = dragMove.dayIdx;
        for (let i = 0; i < cols.length; i++) {
          const rect = cols[i].getBoundingClientRect();
          if (e.clientX >= rect.left && e.clientX < rect.right) {
            newDayIdx = i;
            break;
          }
        }
        const rect = cols[newDayIdx].getBoundingClientRect();
        const y = e.clientY - rect.top - dragMove.offsetY;
        const snappedHour = yToHour(y);
        setDragMove(prev => ({ ...prev, dayIdx: newDayIdx, currentTop: snappedHour * HOUR_HEIGHT }));
      }
    }
  }, [dragCreate, dragMove, yToHour]);

  const handleGridMouseUp = useCallback(() => {
    if (dragCreate) {
      const startHour = yToHour(Math.min(dragCreate.startY, dragCreate.currentY));
      const endHour = yToHour(Math.max(dragCreate.startY, dragCreate.currentY));
      if (endHour - startHour >= 0.25) {
        const date = weekDays[dragCreate.dayIdx];
        const startDate = new Date(date);
        startDate.setHours(Math.floor(startHour), Math.round((startHour % 1) * 60), 0, 0);
        const endDate = new Date(date);
        endDate.setHours(Math.floor(endHour), Math.round((endHour % 1) * 60), 0, 0);
        onCreateEventRange?.({ startDate, endDate });
      }
      setDragCreate(null);
    }
    if (dragMove) {
      const newHour = yToHour(dragMove.currentTop);
      const date = weekDays[dragMove.dayIdx];
      const origStart = new Date(dragMove.event.start_time);
      const origEnd = new Date(dragMove.event.end_time);
      const duration = origEnd - origStart;

      const newStart = new Date(date);
      newStart.setHours(Math.floor(newHour), Math.round((newHour % 1) * 60), 0, 0);
      const newEnd = new Date(newStart.getTime() + duration);

      if (newStart.getTime() !== origStart.getTime()) {
        onEventUpdate?.(dragMove.event.id, {
          startTime: newStart.toISOString(),
          endTime: newEnd.toISOString()
        });
      }
      setDragMove(null);
    }
  }, [dragCreate, dragMove, weekDays, yToHour, onCreateEventRange, onEventUpdate]);

  // Global mouse listeners for drag
  useEffect(() => {
    if (dragCreate || dragMove) {
      window.addEventListener('mousemove', handleGridMouseMove);
      window.addEventListener('mouseup', handleGridMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleGridMouseMove);
        window.removeEventListener('mouseup', handleGridMouseUp);
      };
    }
  }, [dragCreate, dragMove, handleGridMouseMove, handleGridMouseUp]);

  // Event drag start
  const handleEventDragStart = (e, event, dayIdx) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const startH = new Date(event.start_time).getHours() + new Date(event.start_time).getMinutes() / 60;
    setDragMove({
      event,
      offsetY,
      dayIdx,
      currentTop: startH * HOUR_HEIGHT
    });
  };

  const hasAllDay = weekDays.some(d => getEventsForDay(d).allDay.length > 0);

  // Drag-create selection rect
  const getDragCreateRect = () => {
    if (!dragCreate) return null;
    const top = Math.min(dragCreate.startY, dragCreate.currentY);
    const bottom = Math.max(dragCreate.startY, dragCreate.currentY);
    const startH = yToHour(top);
    const endH = yToHour(bottom);
    return {
      top: startH * HOUR_HEIGHT,
      height: (endH - startH) * HOUR_HEIGHT,
      label: `${hourToTime(startH)} – ${hourToTime(endH)}`
    };
  };

  return (
    <div className="flex flex-col h-full bg-white select-none">
      {/* Day column headers */}
      <div className="flex border-b border-gray-200 flex-shrink-0">
        <div className="w-[56px] flex-shrink-0" />
        {weekDays.map((date, idx) => (
          <div key={idx} className="flex-1 py-2 text-center border-l border-gray-100">
            <div className={`text-[11px] font-medium tracking-wider ${isToday(date) ? 'text-blue-600' : 'text-gray-500'}`}>
              {date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}
            </div>
            <div className={`text-[26px] font-normal leading-tight mt-0.5 ${isToday(date) ? 'w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto' : 'text-gray-800'}`}>
              {date.getDate()}
            </div>
          </div>
        ))}
      </div>

      {/* All-day row */}
      {hasAllDay && (
        <div className="flex border-b border-gray-200 min-h-[28px] flex-shrink-0">
          <div className="w-[56px] flex-shrink-0 text-[10px] text-gray-500 text-right pr-2 pt-1">all-day</div>
          {weekDays.map((date, idx) => {
            const { allDay } = getEventsForDay(date);
            return (
              <div key={idx} className="flex-1 border-l border-gray-100 px-0.5 py-0.5 space-y-px">
                {allDay.map(event => (
                  <button key={event.id} onClick={() => onEventClick?.(event)}
                    className="w-full text-left text-[11px] px-1.5 py-px rounded text-white font-medium truncate hover:opacity-90"
                    style={{ backgroundColor: getColor(event) }}>
                    {event.title}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Time grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div ref={gridRef} className="flex" style={{ minHeight: HOURS.length * HOUR_HEIGHT }}>
          {/* Time gutter */}
          <div className="w-[56px] flex-shrink-0 relative">
            {HOURS.map(hour => (
              <div key={hour} className="text-[10px] text-gray-500 text-right pr-2 relative" style={{ height: HOUR_HEIGHT }}>
                {hour > 0 && <span className="absolute -top-[6px] right-2">{formatHour(hour)}</span>}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map((date, dayIdx) => {
            const { timed } = getEventsForDay(date);
            const createRect = dragCreate?.dayIdx === dayIdx ? getDragCreateRect() : null;

            return (
              <div
                key={dayIdx}
                data-day-col
                className={`flex-1 relative border-l border-gray-100 ${isToday(date) ? 'bg-blue-50/20' : ''}`}
                style={{ minHeight: HOURS.length * HOUR_HEIGHT, cursor: dragCreate ? 'ns-resize' : 'default' }}
                onMouseDown={(e) => handleGridMouseDown(e, dayIdx)}
              >
                {/* Hour grid lines */}
                {HOURS.map(hour => (
                  <div key={hour} className="border-b border-gray-100" style={{ height: HOUR_HEIGHT }} />
                ))}

                {/* Drag-to-create selection */}
                {createRect && (
                  <div
                    className="absolute left-1 right-1 bg-blue-200/60 border border-blue-400 rounded-md z-30 pointer-events-none"
                    style={{ top: createRect.top, height: Math.max(createRect.height, 15) }}
                  >
                    <span className="text-[10px] text-blue-700 font-medium px-1">{createRect.label}</span>
                  </div>
                )}

                {/* Timed events */}
                {timed.map(event => {
                  const color = getColor(event);
                  const colWidth = 100 / event._totalCols;
                  const left = event._col * colWidth;
                  const isDragging = dragMove?.event.id === event.id;
                  const top = isDragging && dragMove.dayIdx === dayIdx ? dragMove.currentTop : event.top;

                  return (
                    <div
                      key={event.id}
                      className={`absolute rounded-md px-1.5 py-0.5 text-left overflow-hidden border-l-[3px] cursor-grab transition-shadow ${isDragging ? 'shadow-lg opacity-80 z-40' : 'hover:shadow-md z-10'}`}
                      style={{
                        top,
                        height: event.height,
                        left: `calc(${left}% + 2px)`,
                        width: `calc(${colWidth}% - 4px)`,
                        backgroundColor: lighten(color),
                        borderLeftColor: color,
                      }}
                      onMouseDown={(e) => handleEventDragStart(e, event, dayIdx)}
                      onClick={(e) => { if (!dragMove) { e.stopPropagation(); onEventClick?.(event); } }}
                    >
                      <div className="text-[11px] font-medium truncate" style={{ color }}>{event.title}</div>
                      {event.height > 36 && (
                        <div className="text-[10px] text-gray-500">
                          {new Date(event.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Current time line */}
                {isToday(date) && (
                  <div className="absolute left-0 right-0 z-20 pointer-events-none"
                    style={{ top: (today.getHours() + today.getMinutes() / 60) * HOUR_HEIGHT }}>
                    <div className="absolute -left-[5px] -top-[5px] w-[10px] h-[10px] bg-red-500 rounded-full" />
                    <div className="border-t-2 border-red-500" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
